import { isPromoCard } from "../promo-cards";

type MarketplaceHit = {
    objectID?: string | number;
    id?: string | number;
    __promo?: boolean;
};

export type MarketplaceSsrRoot = {
    dataset: {
        ssrPage?: string;
        ssrFirstHitId?: string;
        ssrQueryKey?: string;
        /** Identifiants des véhicules rendus par le serveur, dans l'ordre (« 104,7,8 »). */
        ssrHitIds?: string;
    };
};

function firstVehicleHitId(hits: MarketplaceHit[]): string | null {
    const hit = hits.find((entry) => !isPromoCard(entry));
    if (!hit) {
        return null;
    }

    return String(hit.objectID ?? hit.id ?? "");
}

/**
 * True while Blade SSR grid still matches the current InstantSearch response.
 * Must stay true across subsequent renders until the query/filters diverge.
 */
export function shouldKeepMarketplaceSsrGrid(
    ssrRoot: MarketplaceSsrRoot | null | undefined,
    results: { page?: number } | null | undefined,
    hits: MarketplaceHit[],
    configQueryKey?: string | null,
    ssrAlreadyRemoved = false,
): boolean {
    if (ssrAlreadyRemoved || !ssrRoot) {
        return false;
    }

    const ssrPage = Number.parseInt(ssrRoot.dataset.ssrPage ?? "0", 10);
    const clientPage = results?.page ?? 0;
    if (clientPage !== (Number.isNaN(ssrPage) ? 0 : ssrPage)) {
        return false;
    }

    const ssrFirstId = ssrRoot.dataset.ssrFirstHitId ?? "";
    if (ssrFirstId === "") {
        return false;
    }

    const domQueryKey = ssrRoot.dataset.ssrQueryKey ?? "";
    if (domQueryKey !== "") {
        if (!configQueryKey || configQueryKey !== domQueryKey) {
            return false;
        }
    }

    if (firstVehicleHitId(hits) !== ssrFirstId) {
        return false;
    }

    return clientHitsCoverSsrGrid(ssrRoot.dataset.ssrHitIds, hits);
}

/**
 * Les véhicules rendus par le serveur sont, dans l'ordre, les premiers de la réponse client :
 * sinon la grille SSR montrerait des cartes que la recherche ne renvoie pas.
 * Sans liste (ancien HTML en cache), seule la comparaison du premier véhicule s'applique.
 */
export function clientHitsCoverSsrGrid(
    ssrHitIds: string | undefined,
    hits: MarketplaceHit[],
): boolean {
    if (ssrHitIds === undefined || ssrHitIds === "") {
        return true;
    }

    const ssrIds = ssrHitIds.split(",");
    const clientIds = vehicleHitIdsKey(hits).split(",");
    if (clientIds.length < ssrIds.length) {
        return false;
    }

    return ssrIds.every((id, index) => clientIds[index] === id);
}

/** Identifiants des véhicules d'une réponse, dans l'ordre (cartes promo exclues). */
export function vehicleHitIdsKey(hits: MarketplaceHit[]): string {
    return hits
        .filter((entry) => !isPromoCard(entry))
        .map((hit) => String(hit.objectID ?? hit.id ?? ""))
        .join(",");
}

/**
 * Après la réconciliation, la grille SSR ne reste que tant que la réponse liste exactement les
 * mêmes véhicules : un filtre qui garde le même premier véhicule (plage de mensualité, de prix…)
 * doit quand même remplacer les cartes.
 */
export function ssrGridStillMatches(
    reconciledHitIdsKey: string | null,
    hits: MarketplaceHit[],
): boolean {
    return reconciledHitIdsKey === null || reconciledHitIdsKey === vehicleHitIdsKey(hits);
}
